import { Options, Table } from '@ckprivate/ckf-ui';
import { ActionIcon, Button, Group } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconTrash, IconX } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { type DataTableColumn } from 'mantine-datatable';
import React, { useMemo, useState } from 'react';

import MoreItemsBadge from '../../../../../components/badges/MoreItemsBadge';
import CustomTooltip from '../../../../../components/CustomTooltip';
import { useI18n } from '../../../../../contexts/I18nProvider';
import BlockingPeriodsService from '../../../../../services/BlockingPeriodsService';
import type { BlockingPeriodResponse } from '../../../../../types/api/response/blockingPeriod';
import type { CompanyResponse } from '../../../../../types/api/response/company';
import handleErrorMessage from '../../../../../utils/handleErrorMessage';
import {
  getHiddenColumns,
  saveHiddenColumns,
} from '../../../../../utils/optionsPreferences';
import { NO_WORK_UNIT } from '../../../../../variables/GlobalVariables';

type Props = {
  companyBlockingPeriodsData: BlockingPeriodResponse[];
  isCompanyBlockingPeriodsListLoading: boolean;
  refetchCompanyBlockingPeriodsData: () => void;
  company: CompanyResponse;
};

const componentName = 'BlockingPeriodsTable';
export default function BlockingPeriodsTable({
  companyBlockingPeriodsData,
  isCompanyBlockingPeriodsListLoading,
  refetchCompanyBlockingPeriodsData,
  company,
}: Props) {
  const { t, lang } = useI18n();
  const [selectedBlockingPeriods, setSelectedBlockingPeriods] = useState<
    BlockingPeriodResponse[]
  >([]);
  const [options, setOptions] = useState<Options>({
    sort: { columnAccessor: '', direction: 'asc' },
    search: {
      accessor: 'name',
      label: t('w.name'),
    },
    hiddenColumns: getHiddenColumns({ componentName }),
    updateHiddenColumns,
  });

  const { mutate: deleteBlockingPeriod } = useMutation({
    mutationFn: (variables: { blockingPeriodId: string }) =>
      BlockingPeriodsService.deleteBlockingPeriod(
        variables.blockingPeriodId,
        company.id
      ),
    onSuccess: () => {
      refetchCompanyBlockingPeriodsData();
      showNotification({
        id: `delete-blocking-periods-successful`,
        title: t('w.success'),
        message: t('w.deleted'),
        color: 'green',
        icon: <IconCheck />,
      });
      setSelectedBlockingPeriods([]);
    },
    onError: (error) =>
      showNotification({
        id: 'delete-blocking-periods-error',
        title: t('w.error'),
        message: handleErrorMessage(error, t),
        color: 'red',
        icon: <IconX />,
      }),
  });

  function updateHiddenColumns(newHiddenColumns: string[]) {
    const _options = { ...options };
    _options.hiddenColumns = newHiddenColumns;
    saveHiddenColumns({ componentName, hiddenColumns: newHiddenColumns });
    setOptions(_options);
  }

  const blockingPeriodsList = useMemo(() => {
    if (!companyBlockingPeriodsData) {
      return [];
    }
    return companyBlockingPeriodsData;
  }, [companyBlockingPeriodsData]);

  const columns: DataTableColumn<BlockingPeriodResponse>[] = useMemo(
    () => [
      {
        accessor: 'name',
        title: t('w.name'),
        sortable: true,
        ellipsis: true,
      },
      {
        accessor: 'description',
        title: 'Description',
        sortable: true,
        ellipsis: true,
      },
      {
        accessor: 'period.start',
        title: t('w.startDate'),
        sortable: true,
        ellipsis: true,
        render: ({ period }) => dayjs(period.start).utc().format('DD/MM/YYYY'),
      },
      {
        accessor: 'period.end',
        title: t('w.endDate'),
        sortable: true,
        ellipsis: true,
        render: ({ period }) => dayjs(period.end).utc().format('DD/MM/YYYY'),
      },
      {
        accessor: 'divisions',
        title: company?.divisionLabel ? company.divisionLabel : t('w.unit'),
        sortable: true,
        ellipsis: true,
        render: ({ divisions }) => {
          if (divisions?.length) {
            return (
              <MoreItemsBadge
                items={divisions?.map((div) =>
                  div.name === NO_WORK_UNIT ? t('NO_WORK_UNIT') : div.name
                )}
              />
            );
          }

          return '';
        },
      },
      {
        accessor: 'actions',
        title: t('w.actions'),
        textAlign: 'right',
        sortable: false,
        width: '0%',
        render: ({ id }) => (
          <Group gap={4} justify="end" wrap={'nowrap'}>
            <CustomTooltip label={t('w.delete')}>
              <ActionIcon
                variant={'subtle'}
                size="sm"
                color="red"
                onClick={() => deleteBlockingPeriod({ blockingPeriodId: id })}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </CustomTooltip>
          </Group>
        ),
      },
    ],
    [company?.divisionLabel]
  );

  const rightCustomActions = useMemo(
    () => [
      <Button
        leftSection={<IconTrash />}
        disabled={selectedBlockingPeriods.length === 0}
        key={'delete-several-blocking-periods'}
        onClick={handleDeleteSelectedBlockingPeriodsClick}
      >
        {t('w.delete')}
      </Button>,
    ],
    [selectedBlockingPeriods.length]
  );

  function handleDeleteSelectedBlockingPeriodsClick() {
    selectedBlockingPeriods.forEach((item) =>
      deleteBlockingPeriod({ blockingPeriodId: item.id })
    );
  }

  return (
    <Table
      height={'calc(100vh - 220px)'}
      pinFirstColumn
      pinLastColumn
      onSelect={(blockingPeriod) =>
        setSelectedBlockingPeriods(blockingPeriod as BlockingPeriodResponse[])
      }
      rows={blockingPeriodsList}
      fetching={isCompanyBlockingPeriodsListLoading}
      options={options}
      columns={columns}
      lang={lang}
      rightCustomActions={rightCustomActions}
      withTableBorder={false}
      width="0vw" // autoscale width
    />
  );
}
